/**
 * Contacts Import Service
 * 
 * Business logic for importing contacts.
 * - NO UI management
 * - NO permission management
 * - Only handles: reading contacts from Flutter, sending to backend, handling responses
 */

class ContactsImportService {
    static REQUEST_TIMEOUT_MS = 120000 // 2 minutes

    /**
     * Import contacts from device and send to backend
     * @param {string} apiUrl - Backend API endpoint
     * @returns {Promise<{status: string, ...}>}
     *   - { status: "processing", message: string, total_count: number }
     *   - { status: "success", imported_count: number, updated_count: number }
     *   - { status: "error", message: string }
     */
    static async import(apiUrl = "/api/v1/contacts/import") {
        // Step 1: Get contacts from Flutter
        const deviceResult = await this.getContactsFromDevice()
        
        if (!deviceResult.success) {
            return {
                status: "error",
                message: deviceResult.error || "Erreur lors de la lecture des contacts"
            }
        }

        if (!deviceResult.contacts || deviceResult.contacts.length === 0) {
            return {
                status: "error",
                message: "Aucun contact à importer"
            }
        }

        // Step 2: Send to backend
        return await this.sendToBackend(apiUrl, deviceResult.contacts)
    }

    /**
     * Get contacts from device using FlutterPermissions helper
     * @returns {Promise<{success: boolean, contacts: array, error?: string}>}
     */
    static async getContactsFromDevice() {
        if (!window.FlutterPermissions) {
            return {
                success: false,
                contacts: [],
                error: "FlutterPermissions helper not available"
            }
        }

        if (!window.FlutterPermissions.isInApp) {
            return {
                success: false,
                contacts: [],
                error: "Not in mobile app"
            }
        }

        try {
            const result = await window.FlutterPermissions.importContacts()
            return result
        } catch (error) {
            console.error("[ContactsImportService] Error getting contacts:", error)
            return {
                success: false,
                contacts: [],
                error: error.message
            }
        }
    }

    /**
     * Send contacts to backend
     * @param {string} apiUrl - Backend API endpoint
     * @param {Array} contacts - Array of contact objects
     * @returns {Promise<{status: string, ...}>}
     */
    static async sendToBackend(apiUrl, contacts) {
        try {
            const csrfToken = document.querySelector('[name="csrf-token"]')?.content

            if (!csrfToken) {
                return {
                    status: "error",
                    message: "CSRF token not found"
                }
            }

            // Create AbortController for timeout
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT_MS)

            try {
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": csrfToken,
                        "Accept": "application/json"
                    },
                    body: JSON.stringify({ contacts: contacts }),
                    signal: controller.signal
                })

                clearTimeout(timeoutId)

                if (!response.ok) {
                    // Handle 504 Gateway Timeout specifically
                    if (response.status === 504) {
                        return {
                            status: "error",
                            message: "Le serveur prend trop de temps à répondre. Les contacts sont peut-être en cours de traitement."
                        }
                    }
                    return {
                        status: "error",
                        message: `Erreur serveur (${response.status})`
                    }
                }

                const responseData = await response.json()

                // Handle processing status (background job)
                if (responseData.status === "processing") {
                    return {
                        status: "processing",
                        message: responseData.message || `Import de ${responseData.total_count} contacts en cours...`,
                        total_count: responseData.total_count
                    }
                }

                // Handle success
                if (responseData.success) {
                    return {
                        status: "success",
                        imported_count: responseData.imported_count || 0,
                        updated_count: responseData.updated_count || 0,
                        total_count: responseData.total_count || 0
                    }
                }

                // Handle error response
                return {
                    status: "error",
                    message: responseData.error || "Erreur lors de l'importation"
                }

            } catch (fetchError) {
                clearTimeout(timeoutId)

                if (fetchError.name === "AbortError") {
                    return {
                        status: "error",
                        message: "L'import prend trop de temps. Les contacts sont peut-être en cours de traitement en arrière-plan."
                    }
                }
                throw fetchError
            }
        } catch (error) {
            console.error("[ContactsImportService] Error sending to backend:", error)
            return {
                status: "error",
                message: error.message || "Erreur réseau"
            }
        }
    }
}

export default ContactsImportService

